/**
 * 전역 캐시 매니저
 * - 로그인 / 페이지 새로고침 / 마스터 수정 후 Bundle 버전 체크로 갱신
 * - GET /java/bundle/v1/latest + (필요 시) GCS gzip JSON
 * - bulk / 주기 자동 갱신 없음
 */

import { getBundle } from '../bundle/getBundle';
import { clearBundleRecord } from '../bundle/bundleStorage';
import {
  extractPermissionsFromBundle,
  hasPermissionEntries,
  BUNDLE_DISTRIBUTED_EVENT,
} from '../bundle/permissionsUtils';

export const BUNDLE_INFO_CACHE_KEYS = [
  'item',
  'delivery',
  'manager',
  'truck',
  'buyer',
  'supplier',
  'vendor',
  'locations',
  'uom_conversion',
  'item_taxon',
  'uom',
  'machine',
];

export { BUNDLE_DISTRIBUTED_EVENT };

class GlobalCacheManager {
  constructor() {
    this.caches = new Map();
    this.isInitialized = false;
    this.pendingInitializePromise = null;
    this.isRefreshing = false;
    this.pendingBundlePromise = null;
    this.lastDistributedPermissions = null;
  }

  register(cacheKey, cacheInstance) {
    if (!cacheInstance.fetchData || typeof cacheInstance.fetchData !== 'function') {
      console.error(`[GlobalCacheManager] Cache "${cacheKey}" must have a fetchData method`);
      return;
    }
    this.caches.set(cacheKey, cacheInstance);
  }

  unregister(cacheKey) {
    this.caches.delete(cacheKey);
  }

  getCache(cacheKey) {
    return this.caches.get(cacheKey) || null;
  }

  /**
   * attachmentImage 싱글톤 확보 (Map 미등록이어도 setFromBundle 가능)
   */
  getAttachmentImageCache() {
    let cache = this.caches.get('attachmentImage');
    if (cache?.setFromBundle) {
      return cache;
    }
    // 순환 import 회피: 런타임에만 로드
    // eslint-disable-next-line global-require
    const mod = require('./attachmentImageCache');
    cache = mod.default || mod;
    if (cache?.fetchData) {
      this.register('attachmentImage', cache);
    }
    return cache;
  }

  emitBundleDistributed(permissions, version) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent(BUNDLE_DISTRIBUTED_EVENT, {
        detail: { permissions: permissions || {}, version },
      })
    );
  }

  /**
   * 인증 플래그 ON 직후 호출 — PermissionContext 리스너가 붙은 뒤 권한 재전달
   */
  republishPermissions() {
    const attachmentCache = this.getAttachmentImageCache();
    const cached = attachmentCache?.getData?.();
    const permissions =
      (hasPermissionEntries(cached?.permissions) && cached.permissions) ||
      this.lastDistributedPermissions ||
      {};
    this.emitBundleDistributed(permissions, cached?.version);
    return permissions;
  }

  /**
   * Bundle JSON → domain caches + permissions/mes (price·notice 제외)
   */
  distributeFromBundle(bundle) {
    if (!bundle) return;

    const permissions = extractPermissionsFromBundle(bundle);
    this.lastDistributedPermissions = permissions;

    if (!hasPermissionEntries(permissions)) {
      console.warn(
        '[GlobalCacheManager] Bundle permissions empty or unrecognized shape. Top-level keys:',
        bundle && typeof bundle === 'object' ? Object.keys(bundle) : bundle,
        'raw permissions:',
        bundle?.permissions
      );
    } else {
      console.info(
        '[GlobalCacheManager] distributing permissions categories:',
        Object.keys(permissions)
      );
    }

    const attachmentCache = this.getAttachmentImageCache();
    if (attachmentCache && typeof attachmentCache.setFromBundle === 'function') {
      attachmentCache.setFromBundle({
        mes: bundle.mes ?? {},
        permissions,
      });
    } else {
      console.error(
        '[GlobalCacheManager] attachmentImage cache unavailable — permissions will not reach UI'
      );
    }

    const info = bundle.info || {};
    const mapping = {
      item: info.item,
      delivery: info.delivery,
      manager: info.manager,
      truck: info.truck,
      buyer: info.buyer,
      supplier: info.supplier,
      vendor: info.vendor,
      locations: info.locations,
      uom_conversion: info.uom_conversion,
      item_taxon: info.item_taxon,
      uom: info.uom,
      machine: info.machine,
    };

    for (const [cacheKey, data] of Object.entries(mapping)) {
      const cache = this.caches.get(cacheKey);
      if (cache && typeof cache.setData === 'function') {
        cache.setData(data || []);
      }
    }

    this.emitBundleDistributed(permissions, bundle.version);
  }

  async refreshFromBundle(forceRefresh = false) {
    if (this.pendingBundlePromise) {
      return this.pendingBundlePromise;
    }

    this.pendingBundlePromise = (async () => {
      try {
        await getBundle({
          forceRefresh,
          globalCacheManager: this,
        });
        return true;
      } catch (error) {
        console.error('[GlobalCacheManager] Bundle refresh failed:', error);
        throw error;
      } finally {
        this.pendingBundlePromise = null;
      }
    })();

    return this.pendingBundlePromise;
  }

  async syncBundleAfterMutation() {
    return this.refreshFromBundle(false);
  }

  async initialize(forceRefresh = false) {
    if (this.pendingInitializePromise) {
      return this.pendingInitializePromise;
    }

    if (this.isInitialized && !forceRefresh) {
      return;
    }

    this.pendingInitializePromise = (async () => {
      try {
        this.isInitialized = true;

        if (forceRefresh) {
          this.isRefreshing = true;
        }

        try {
          await this.refreshFromBundle(false);
        } finally {
          if (forceRefresh) {
            setTimeout(() => {
              this.isRefreshing = false;
            }, 1000);
          }
        }
      } finally {
        this.pendingInitializePromise = null;
      }
    })();

    return this.pendingInitializePromise;
  }

  async refreshAll() {
    await this.initialize(true);
  }

  async cleanup() {
    try {
      await clearBundleRecord();
    } catch (error) {
      console.warn('[GlobalCacheManager] Failed to clear bundle storage:', error);
    }

    Array.from(this.caches.values()).forEach((cache) => {
      if (cache.invalidate && typeof cache.invalidate === 'function') {
        cache.invalidate();
      }
    });

    // 등록은 유지 (모듈 싱글톤) — Map clear 시 재로그인 후 Bundle 분배 불가
    this.isInitialized = false;
    this.pendingBundlePromise = null;
    this.pendingInitializePromise = null;
    this.isRefreshing = false;
    this.lastDistributedPermissions = null;
  }

  invalidateCache(cacheKey) {
    const cache = this.caches.get(cacheKey);
    if (cache && cache.invalidate && typeof cache.invalidate === 'function') {
      cache.invalidate();
    }
  }
}

const globalCacheManager = new GlobalCacheManager();

export default globalCacheManager;
